<script setup lang="ts">
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { GoaInputs, OrgNameDto } from "~/api";
import { getEnvConfig } from "~/services/appConfig";
import { apiClient } from "~/utils/apiClient";
import { dateOnly } from "~/utils/dateUtils";

import { useMyInfoStore } from "~/stores/MyInfoStore";
const myInfoStore = useMyInfoStore();

const housingOrgs = ref<OrgNameDto[]>([]);

async function refreshHousingOrgs(): Promise<any> {
  await apiClient.dataExtract.getOrgNames().then((orgs) => {
    housingOrgs.value = orgs;
  });
}

const getQuarterRange = (
  referenceDate: Date,
  quartersAgo: number,
): [Date, Date] => {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth(); // JavaScript months are 0-based
  const currentQuarter = Math.floor(month / 3); // 0: Q1, 1: Q2, 2: Q3, 3: Q4

  // Calculate the target quarter and year
  const targetQuarter = (currentQuarter - quartersAgo + 4) % 4; // Wrap around using modulo
  const yearOffset = Math.floor((currentQuarter - quartersAgo) / 4); // Adjust year if we wrap around

  const targetYear = year + yearOffset;

  // Calculate start and end dates of the target quarter
  const startMonth = targetQuarter * 3; // Quarter's first month (0-based)
  const startDate = new Date(targetYear, startMonth, 1); // First day of the quarter
  const endDate = new Date(targetYear, startMonth + 3, 0); // Last day of the quarter

  return [startDate, endDate];
};

// make ranges for the calendar quarters
const dateRangeShortcuts = [
  {
    text: "This Quarter",
    value: () => {
      return getQuarterRange(new Date(), 0);
    },
  },
  {
    text: "Last Quarter",
    value: () => {
      // get the last calendar quarter
      return getQuarterRange(new Date(), 1);
    },
  },
  {
    text: "Last Year",
    value: () => {
      const end = new Date(new Date().getFullYear() - 1, 11, 31);
      const start = new Date(end.getFullYear(), 0, 1);
      return [start, end];
    },
  },
];

type logEntry = {
  text: string;
  type: "main" | "sub";
};
const localLog = ref<logEntry[]>([]);

const housingOrgGuid = ref<string>("");
const dateRange = ref(getQuarterRange(new Date(), 1));

const canReport = computed(() => {
  return (
    housingOrgGuid.value !== null &&
    dateRange.value[0] !== null &&
    dateRange.value[1] !== null
  );
});

const getReport = async () => {
  if (!canReport.value) {
    return;
  }

  const [startDate, endDate] = dateRange.value;
  const sysOrgGuid = housingOrgGuid.value;

  if (!sysOrgGuid || startDate === null || endDate === null) {
    return;
  }

  // call server
  const goaInputs: GoaInputs = {
    housingOrgGuid: sysOrgGuid,
    dateFrom: dateOnly(startDate),
    dateTo: dateOnly(endDate),
  };

  ElMessage({
    message: "Preparing... ",
    grouping: true,
    showClose: true,
    duration: 0,
  });

  apiClient.dataExtract.extractForGoa(goaInputs).then((exportResult) => {
    ElMessage.closeAll();

    if (exportResult.success) {
      localLog.value.push({ text: exportResult.message ?? "?", type: "main" });
      if (exportResult.logMessages) {
        localLog.value.push(
          ...exportResult.logMessages.map(
            (msg: string): logEntry => ({
              text: msg,
              type: "sub",
            }),
          ),
        );
      }
      ElMessage({
        message: exportResult.message ?? "Downloaded",
        grouping: true,
        showClose: true,
      });

      const serverUrl = getEnvConfig().serverUrl;
      location.href = `${serverUrl}/General/DownloadExport?key=${exportResult.key}`;
    } else {
      localLog.value.push({ text: exportResult.message ?? "?", type: "main" });
      ElMessage({
        type: "warning",
        message: exportResult.message ?? "Failed to download",
        grouping: true,
        showClose: true,
      });
    }
  });
};

onMounted(async () => {
  await Promise.all([refreshHousingOrgs(), myInfoStore.load()]);
});
</script>

<template>
  <div class="DataExtracts">
    <h2>Wait List Report</h2>
    <h3>Government of Alberta Format</h3>
    <p>
      This includes everyone who was on the wait list during the reporting
      period.
    </p>
    <div class="form">
      <label>Housing Org</label>
      <el-select v-model="housingOrgGuid" placeholder="Select a housing org">
        <el-option
          v-for="org in housingOrgs"
          :key="org.housingOrgGuid"
          :label="org.name ?? '?'"
          :value="org.housingOrgGuid"
        ></el-option>
      </el-select>
      <label>Reporting Period</label>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="To"
        :shortcuts="dateRangeShortcuts"
        start-placeholder="Start date"
        end-placeholder="End date"
      ></el-date-picker>
      <div></div>
      <div>
        <el-button type="primary" :disabled="!canReport" @click="getReport"
          >Download JSON file</el-button
        >
        <span class="comment"
          >JSON files can be viewed with a text editor.</span
        >
      </div>
    </div>
    <div>
      <strong>Log</strong>
      &nbsp;
      <el-button size="small" @click="localLog = []">Clear</el-button>
    </div>
    <div class="log">
      <div v-for="log in localLog" :key="log" :class="log.type">
        {{ log.text }}
      </div>
    </div>
  </div>
</template>

<style lang="less">
.DataExtracts {
  padding: 20px;
  .form {
    max-width: 500px;
    display: grid;
    grid-template-columns: 140px 1fr;
    grid-template-rows: auto;
    gap: 10px 20px;
    align-items: center;
    justify-items: flex-start;
  }

  .comment {
    display: block;
    margin: 8px 0;
    color: var(--el-text-color-placeholder);
    font-size: 0.85em;
  }
  h2 {
    margin: 0;
  }
  h3 {
    margin-top: 0.25em;
    font-size: 1.25em;
  }
  .log {
    margin-left: 1em;
    .main {
      margin-top: 0.5em;
    }
    .sub {
      margin-left: 1em;
      color: var(--el-color-error);
    }
  }
}
</style>
